import {
  DEFAULT_VERTICAL,
  VERTICAL_CODES,
  isVerticalCode,
  type VerticalCode,
} from '@/verticals/codes'
import { config } from './config'

export type Surface =
  | { kind: 'platform' }
  | { kind: 'admin'; vertical: VerticalCode }
  | { kind: 'guest'; vertical: VerticalCode; tenantSlug: string }
  | { kind: 'root'; vertical: VerticalCode }

const OVERRIDE_KEY = 'ag.surface'
const slugPattern = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

function readStoredOverride(): Surface | null {
  try {
    const raw = sessionStorage.getItem(OVERRIDE_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw) as Partial<Surface> & { vertical?: unknown }
    if (stored.kind === 'platform') return { kind: 'platform' }
    const vertical = isVerticalCode(stored.vertical) ? stored.vertical : DEFAULT_VERTICAL
    if (stored.kind === 'admin' || stored.kind === 'root') return { kind: stored.kind, vertical }
    if (stored.kind === 'guest' && 'tenantSlug' in stored && typeof stored.tenantSlug === 'string')
      return { kind: 'guest', vertical, tenantSlug: stored.tenantSlug }
    return null
  } catch {
    return null
  }
}

function storeOverride(surface: Surface) {
  try {
    sessionStorage.setItem(OVERRIDE_KEY, JSON.stringify(surface))
  } catch {
    return
  }
}

function matchDomain(host: string) {
  return VERTICAL_CODES.map((code) => ({ code, domain: config.domains[code] }))
    .filter(({ domain }) => host === domain || host.endsWith(`.${domain}`))
    .sort((a, b) => b.domain.length - a.domain.length)[0]
}

export function resolveSurface(location: Pick<Location, 'hostname' | 'search'>): Surface {
  const params = new URLSearchParams(location.search)
  const tenant = params.get('tenant')?.toLowerCase()
  const forced = params.get('surface')
  const requested = params.get('vertical')
  const vertical = isVerticalCode(requested) ? requested : DEFAULT_VERTICAL
  if (tenant && slugPattern.test(tenant)) {
    const surface: Surface = { kind: 'guest', vertical, tenantSlug: tenant }
    storeOverride(surface)
    return surface
  }
  if (forced === 'platform') {
    const surface: Surface = { kind: 'platform' }
    storeOverride(surface)
    return surface
  }
  if (forced === 'admin' || forced === 'root') {
    const surface: Surface = { kind: forced, vertical }
    storeOverride(surface)
    return surface
  }

  const host = location.hostname.toLowerCase()
  if (host === config.platformHost) return { kind: 'platform' }
  const match = matchDomain(host)
  if (match) {
    if (host === match.domain || host === `www.${match.domain}`) {
      return readStoredOverride() ?? { kind: 'root', vertical: match.code }
    }
    const subdomain = host.slice(0, -(match.domain.length + 1))
    if (subdomain === config.appSubdomain) return { kind: 'admin', vertical: match.code }
    if (slugPattern.test(subdomain)) {
      return { kind: 'guest', vertical: match.code, tenantSlug: subdomain }
    }
  }

  const stored = readStoredOverride()
  if (stored) return stored
  if (config.defaultTenant) {
    return { kind: 'guest', vertical: DEFAULT_VERTICAL, tenantSlug: config.defaultTenant }
  }
  return { kind: 'root', vertical: DEFAULT_VERTICAL }
}

export function surfaceVertical(surface: Surface): VerticalCode {
  return surface.kind === 'platform' ? DEFAULT_VERTICAL : surface.vertical
}

export function brandVertical(surface: Surface): VerticalCode {
  if (surface.kind !== 'platform') return surface.vertical
  return matchDomain(config.platformHost)?.code ?? DEFAULT_VERTICAL
}
